import { keyBy } from 'lodash-es';

import { getNodesAndRelationships } from '../../services/discovery';
import { log } from '../../services/logging';
import { getSchemaFromPerspective } from '../../services/perspectives/schema';
import type { Node, Relationship } from '../../types/graph';
import type { Perspective } from '../../types/perspective';
import type { Style, StyleCategory, StyleRelationshipType } from '../../types/style';
import type { Position } from './scene.types';

export const getStyleRulesCount = (style: Style) => {
  let numStyleRules = 0;
  const countStyleRules = (entries: (StyleCategory | StyleRelationshipType)[]) =>
    entries.reduce((acc, entry) => acc + (entry.styleRules?.length || 0), 0);

  if (style?.categories) {
    numStyleRules += countStyleRules(style.categories);
  }

  if (style?.relationshipTypes) {
    numStyleRules += countStyleRules(style.relationshipTypes);
  }

  return numStyleRules;
};

export const getCombinedNodeIdsWithGrouped = (
  visibleNodes: Position[],
  nodeGroups: Record<string, string[]> = {},
): string[] => {
  const groupedNodeIds = Object.values(nodeGroups).flat();
  return visibleNodes
    .filter(({ id }) => !nodeGroups[id])
    .map(({ id }) => id)
    .concat(groupedNodeIds);
};

export const loadNodesAndRelationships = (
  visibleNodeIds: string[],
  visibleRelationshipIds: string[],
  perspective: Perspective,
  visibleRelationshipTypes: string[],
  visibleLabels: string[],
  isV5OrGreater: boolean,
): Promise<{ nodes: Node[]; relationships: Relationship[] }> =>
  new Promise<{ nodes: Node[]; relationships: Relationship[] }>((resolve, reject) => {
    return getNodesAndRelationships({
      nodeIds: visibleNodeIds,
      relationshipIds: visibleRelationshipIds,
      schema: getSchemaFromPerspective(perspective),
      visibleRelationshipTypes,
      visibleLabels,
      responseHandler: (result: any) => {
        const { nodes, relationships, error } = result;
        if (error) {
          log.error(error.message || error);
          reject(error.message || error);
        } else {
          resolve({ nodes, relationships });
        }
      },
      isV5OrGreater,
    });
  });

export const actualiseNodeAndRelationshipIds = (
  inventoryNodes: Node[],
  inventoryRelationships: Relationship[],
  visibleRelationshipIds: string[],
  nodePositions: { id: string; x: number; y: number }[],
  isV5OrGreater: boolean,
) => {
  const inventoryNodeIdsMap = keyBy(inventoryNodes, 'id');
  const inventoryRelIdsMap = keyBy(inventoryRelationships, 'id');

  const inventoryNodesIdentityToIdMap = inventoryNodes.reduce((acc: Record<string, string>, node) => {
    node.identity && (acc[`${node.identity}`] = node.id);
    return acc;
  }, {});

  // Map old identity id (numeric) to the new elementId version (string), useful when we need to migrate Scenes
  const inventoryRelationshipsIdentityToIdMap = inventoryRelationships.reduce((acc: Record<string, string>, rel) => {
    rel.identity && (acc[`${rel.identity}`] = rel.id);
    return acc;
  }, {});

  const actualPositionNodes: { id: string; x: number; y: number }[] = nodePositions.map((node) => ({
    ...node,
    id: inventoryNodesIdentityToIdMap[node.id] ?? node.id,
  }));

  const actualVisibleNodeIds = inventoryNodes
    .map((node) => inventoryNodesIdentityToIdMap[node.id] ?? node.id)
    .filter((id) => inventoryNodeIdsMap[id]);

  const actualVisibleRelationshipIds = visibleRelationshipIds
    .map((id) => inventoryRelationshipsIdentityToIdMap[id] ?? id)
    .filter((id) => inventoryRelIdsMap[id]);

  return {
    actualVisibleNodeIds,
    actualVisibleRelationshipIds,
    actualPositionNodes,
  };
};
